import React, { Component } from "react";
import { connect } from "react-redux";
import Select from "react-select";
import { Modal, ModalHeader, ModalBody, ModalFooter } from "reactstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import equipmentActions from "../../../../_actions/equipmentActions";

// "(Number) Class / Make / Model Code" — matches the Project Admin Equipment tab's own column
// and the ADR equipment dropdown (getEquipCodeLine in AdrV3/utils/adrUtilsV3.js), so an equipment
// item reads identically wherever it's shown. Falls back to Description for manual entries that
// have no catalog Class/Make/Model.
const getEquipDisplayLabel = (e) => {
  const classMakeModel = [e.equipmentClass, e.equipmentMake, e.equipmentModel]
    .filter(Boolean)
    .join(" / ");
  const numberPart = e.number ? `(${e.number})` : "(No. N/A)";
  return [numberPart, classMakeModel || e.description || ""].filter(Boolean).join(" ");
};

class MergeEquipmentModal extends Component {
  state = {
    targetOption: null,
    merging: false,
    error: null,
  };

  getTargetOptions = () => {
    const { projEquipList, sourceEquipment } = this.props;
    if (!projEquipList || !sourceEquipment) return [];
    return projEquipList
      .filter(
        (e) =>
          e.contractorId === sourceEquipment.contractorId &&
          e.equipmentId !== sourceEquipment.equipmentId
      )
      .map((e) => ({
        value: e.equipmentId,
        label: getEquipDisplayLabel(e),
      }))
      .sort((a, b) => a.label.localeCompare(b.label));
  };

  handleSubmit = async (e) => {
    e.preventDefault();
    const { dispatch, projectId, sourceEquipment, onMergeSuccess } = this.props;
    const { targetOption } = this.state;
    if (!targetOption) return;

    this.setState({ merging: true, error: null });
    try {
      await dispatch(
        equipmentActions.mergeEquipment(
          sourceEquipment.equipmentId,
          targetOption.value,
          projectId,
          sourceEquipment.contractorId
        )
      );
      onMergeSuccess();
    } catch (err) {
      const msg =
        typeof err?.response?.data === "string"
          ? err.response.data
          : "An error occurred while merging equipment.";
      this.setState({ merging: false, error: msg });
    }
  };

  render() {
    const { showModal, toggleModal, sourceEquipment } = this.props;
    const { targetOption, merging, error } = this.state;
    const targetOptions = this.getTargetOptions();

    const sourceLabel = sourceEquipment ? getEquipDisplayLabel(sourceEquipment) : "";

    const closeBtn = (
      <button className="close" onClick={toggleModal} disabled={merging}>
        <FontAwesomeIcon icon="times" className="fa-fw text-dark" />
      </button>
    );

    return (
      <Modal isOpen={showModal} toggle={!merging ? toggleModal : undefined} size="lg" fade={false}>
        <ModalHeader className="bg-warning text-dark" close={closeBtn}>
          Merge Equipment
        </ModalHeader>
        <ModalBody>
          <div className="alert alert-warning p-2 mb-3">
            <FontAwesomeIcon icon="exclamation-triangle" className="fa-fw mr-2" />
            <b>Warning:</b> This permanently merges the source equipment into the target.
            <ul className="mb-0 mt-1">
              <li>Draft and submitted daily reports will be updated to the target equipment.</li>
              <li>Approved daily reports will receive an auto-approved amendment reflecting the change.</li>
              <li>The source equipment will be marked inactive and hidden from new report entries.</li>
            </ul>
          </div>

          <div className="card mb-2">
            <div className="card-body py-2">
              <div className="form-row align-items-center">
                <label className="col-sm-4 col-form-label">
                  <b>Source</b> <small className="text-muted">(merge from)</small>
                </label>
                <div className="col-sm-8">
                  <input
                    className="form-control-plaintext font-weight-bold"
                    readOnly
                    value={sourceLabel}
                  />
                </div>
              </div>
            </div>
          </div>

          <div className="card mb-2">
            <div className="card-body py-2">
              <div className="form-row align-items-center">
                <label className="col-sm-4 col-form-label">
                  <b>Target</b> <small className="text-muted">(merge into)</small>
                </label>
                <div className="col-sm-8">
                  <Select
                    value={targetOption}
                    options={targetOptions}
                    onChange={(opt) => this.setState({ targetOption: opt, error: null })}
                    isSearchable
                    placeholder="Select target equipment..."
                    isDisabled={merging}
                    noOptionsMessage={() => "No other equipment for this contractor"}
                    styles={{ menu: (p) => ({ ...p, zIndex: 9999 }) }}
                  />
                </div>
              </div>
            </div>
          </div>

          {error && (
            <div className="alert alert-danger p-2 mb-0">
              <FontAwesomeIcon icon="exclamation-circle" className="fa-fw mr-1" />
              {error}
            </div>
          )}
        </ModalBody>
        <ModalFooter className="bg-lightgray">
          <div className="form-row form-group mb-0">
            <button
              className="btn btn-warning mr-2"
              onClick={this.handleSubmit}
              disabled={!targetOption || merging}
            >
              {merging ? (
                <>
                  <FontAwesomeIcon icon="spinner" spin className="fa-fw mr-1" />
                  Merging...
                </>
              ) : (
                <>
                  <FontAwesomeIcon className="fa-fw mr-1" />
                  Merge
                </>
              )}
            </button>
            <button className="btn btn-secondary" onClick={toggleModal} disabled={merging}>
              Cancel
            </button>
          </div>
        </ModalFooter>
      </Modal>
    );
  }
}

const mapStateToProps = (state, ownProps) => ({
  projEquipList: state.equipment.projEquipLists[ownProps.projectId] || [],
});

export default connect(mapStateToProps)(MergeEquipmentModal);
